import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getHours, getMinutes, set } from 'date-fns';
import { multischeduleEnabled } from '@utils/consts';
import { useDate } from '@hooks/date';
import { useGetUsersSchedules } from '@services/schedules/internal';
import { useGetUsersSchedule } from '@services/schedules/internal/get';
import { useInternalScheduleForm } from '@forms/schedules/internal';
import { useToast } from '@hooks/useToast';
import { useRemoveInternalSchedule } from '@services/schedules/internal/remove';
import { formatDate } from '@utils/dateHelpers';
import { useSchedulerContext } from '@contexts/scheduler';
import { initialState } from '@contexts/scheduler/constants';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { initialState as sidebarInitialState } from '@contexts/scheduler/sidebar/constants';
import { SchedulerSidebarContextData } from '@contexts/scheduler/sidebar/types';
import { useRescheduler } from '@contexts/rescheduler';
import { handleRelatedSchedulesResponse } from '@contexts/rescheduler/helpers';
import { ReschedulerContextData } from '@contexts/rescheduler/types';
import { toTenantDate } from '@utils/timeZone';
import { useQueryParams } from '@hooks/queryParams';
import { useDropdownsContext } from '@contexts/dropdowns';
import {
  getDisplayOptionsSettings,
  getWorkloadDisplayOptionsSettingsEndpoint,
} from '@components/workload/drawer/displayOptions/helpers';
import { useAppContext } from '@contexts/app';
import { getMultiDayPosition, isExistingMultiDay } from '@contexts/scheduler/sidebar/helpers';
import { Conflict } from '@components/schedules/dialogs/internal/publish/types';
import {
  getJobSubtitle,
  getSkipWeekendDefaults,
} from '@components/schedules/dialogs/internal/helpers';
import { getMultiDayDates, getSeriesDays } from '@forms/schedules/internal/helpers';
import { TimeRange } from '@components/scheduling/timeRangePicker/types';
import {
  getSeriesCalendarModifiers,
  hasSeriesCalendarDays,
} from '@components/schedules/dialogs/internal/seriesCalendar/helpers';
import { UseInternalSchedulerParams } from './types';

export const useInternalScheduler = ({
  open,
  setOpen,
  type,
  schedule,
  jobId,
  initialDate,
  readOnly,
  initialParams = {},
}: UseInternalSchedulerParams) => {
  const { t } = useTranslation('app');
  const { displayOptions, tenant } = useAppContext();
  const { toast } = useToast();
  const { active, ...scheduler } = useSchedulerContext();
  const {
    setState: setSidebarState,
    relatedMultischedules,
    currentSchedule,
    plannedDays,
    extensionDates,
  } = useSchedulerSidebar() as SchedulerSidebarContextData;
  const rescheduler = useRescheduler();
  const [overlap, setOverlap] = useState(false);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [specialtyOverride, setSpecialtyOverride] = useState(false);
  const [remove, setRemove] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedForemen, setSelectedForemen] = useState<string[]>(initialParams.users || []);
  const [isInitialized, setIsInitialized] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const {
    params: { allUsers, type: calendarType },
    setParam,
  } = useQueryParams();

  const { specialties, fieldUsers } = useDropdownsContext();

  const skipSpecialtyConfirmation = getDisplayOptionsSettings(
    displayOptions || [],
    getWorkloadDisplayOptionsSettingsEndpoint(calendarType),
  )?.schedulingSettingsHideMissingSpecialties;

  const { date, rawDate, setRawDate } = useDate({
    initialDate,
    disableLoadFromParams: true,
    disableParamsUpdate: true,
  });

  const {
    data,
    error: schedulesError,
    isFetching: schedulesFetching,
  } = useGetUsersSchedules({
    date: formatDate(date),
    specialties: selectedSpecialties,
    users: selectedForemen,
    active: open && isInitialized,
    crewTypes: ['field_user'],
    divisionIds: [schedule?.task?.job?.division?.id || ''],
    scheduleId: schedule?.id,
  });

  const { error: currentScheduleError } = useGetUsersSchedule(
    open && schedule?.id !== 'new' ? schedule?.id : undefined,
  );

  const removeSchedule = useRemoveInternalSchedule({
    id: active.schedule.id,
    taskId: active.schedule.task?.id || '',
    jobId,
    callback: ({ success }) => {
      if (!success) return;
      setOpen(false);
    },
  });

  const { form, onSubmit, locked } = useInternalScheduleForm({
    successHandler: (resp) => {
      const draftSent = schedule?.status === 'draft' && resp?.data?.status !== schedule?.status;
      form.reset();
      setParam('allUsers', '');
      setOpen(false);
      setOverlap(false);
      setSpecialtyOverride(false);
      toast({
        title: t(
          `messages.success.schedule.${draftSent ? 'saveAndSend' : schedule?.status ? 'update' : 'create'}`,
        ),
      });
      if (!resp || !rescheduler) return;
      handleRelatedSchedulesResponse(rescheduler as ReschedulerContextData, resp);
    },
    onOverlap: (conflicts) => {
      setOverlap(true);
      setConflicts(conflicts);
    },
    onSpecialtyOverride: () => setSpecialtyOverride(true),
  });

  const { watch, setValue, resetField } = form;

  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
      return;
    }
    setSelectedSpecialties(type ? [type.value] : []);
    setSelectedForemen(initialParams.users || []);
    if (initialDate) setRawDate(toTenantDate(initialDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type]);

  useEffect(() => {
    if (!open || isInitialized) return;

    const specialtiesReady =
      JSON.stringify(selectedSpecialties) === JSON.stringify(type ? [type.value] : []);
    const foremenReady =
      JSON.stringify(selectedForemen) === JSON.stringify(initialParams.users || []);
    const dateReady = !initialDate || formatDate(date) === formatDate(toTenantDate(initialDate));

    if (specialtiesReady && foremenReady && dateReady) setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isInitialized, selectedSpecialties, selectedForemen, date]);

  useEffect(() => {
    if (skipSpecialtyConfirmation) {
      setValue('forceSpecialty', skipSpecialtyConfirmation);
      return;
    }
    if (!open || !type?.value) return;
    const arr =
      (active?.schedule?.foreman?.specialties?.length
        ? active?.schedule?.foreman?.specialties?.map((el) => el.value)
        : initialParams.specialties) || [];
    setValue('forceSpecialty', arr.length ? arr.includes(type?.value) : true);
  }, [
    open,
    setValue,
    type?.value,
    active?.schedule?.foreman?.specialties,
    initialParams.specialties,
    skipSpecialtyConfirmation,
  ]);

  useEffect(() => {
    if (!open) return;

    setSidebarState((prev) => ({ ...prev, populateDefaultCrewAndEquipment: true }));

    return () => {
      setSidebarState(sidebarInitialState);
    };
  }, [open, setSidebarState]);

  useEffect(() => {
    if (locked || overlap) return;
    if (!open) {
      resetField('note');
      resetField('status');
      resetField('days');
      resetField('addDays');
      resetField('skipSaturday');
      resetField('skipSunday');
      scheduler.setState(initialState);
      setSidebarState(sidebarInitialState);

      return;
    }

    const foremanId = active.schedule.foreman?.id || schedule?.foreman?.id || '';
    setValue('foremanId', foremanId);

    if (!watch('note')) setValue('note', schedule?.task?.note?.content || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialDate,
    open,
    schedule,
    type,
    locked,
    setValue,
    active.schedule.foreman?.id,
    active.schedule.id,
  ]);

  useEffect(() => {
    if (skipSpecialtyConfirmation) {
      setValue('forceSpecialty', skipSpecialtyConfirmation);
      return;
    }
    if (!selectedForemen.length) return;
    const currentForemanId = active.schedule.foreman?.id || schedule?.foreman?.id;
    if (!currentForemanId) return;

    setValue('forceSpecialty', selectedForemen.includes(currentForemanId));
  }, [
    selectedForemen,
    skipSpecialtyConfirmation,
    setValue,
    schedule?.foreman?.id,
    active.schedule.foreman?.id,
  ]);

  useEffect(() => {
    if (schedule?.id === 'new' || !schedule?.id || ['approved', 'draft'].includes(schedule.status))
      setValue('confirmed', true);
  }, [schedule?.id, schedule?.status, schedule?.task?.id, setValue]);

  const jobSubtitle = getJobSubtitle(schedule);
  const isNewSchedule = !schedule?.id || schedule.id === 'new';
  const canScheduleMultiDay =
    multischeduleEnabled &&
    !readOnly &&
    !schedule?.task?.multischedule &&
    (isNewSchedule || Boolean(currentSchedule?.id)) &&
    !isExistingMultiDay(relatedMultischedules);
  const canExtendMultiDay =
    multischeduleEnabled &&
    !readOnly &&
    !isNewSchedule &&
    Boolean(currentSchedule?.id) &&
    (Boolean(schedule?.task?.multischedule) || isExistingMultiDay(relatedMultischedules));
  const multiDayPosition = getMultiDayPosition({
    relatedMultischedules,
    currentSchedule,
    plannedDays: canScheduleMultiDay ? plannedDays : 1,
  });
  const weekendDefaults = getSkipWeekendDefaults(displayOptions || [], calendarType);
  const [plannedCount, skipSaturday, skipSunday] = watch(['days', 'skipSaturday', 'skipSunday']);
  const isSeries =
    Boolean(currentSchedule?.id) &&
    (Boolean(schedule?.task?.multischedule) || isExistingMultiDay(relatedMultischedules));
  const addedDates = canScheduleMultiDay
    ? getMultiDayDates(rawDate, plannedCount, { skipSaturday, skipSunday }).slice(1)
    : canExtendMultiDay
      ? extensionDates
      : [];
  const seriesDays = isSeries
    ? getSeriesDays({
        relatedMultischedules,
        currentScheduleId: currentSchedule?.id,
        startsAt: active.schedule.startsAt,
        zone: tenant.zone,
      })
    : [];
  const calendarModifiers = getSeriesCalendarModifiers({
    relatedMultischedules: isSeries ? relatedMultischedules : undefined,
    currentScheduleId: currentSchedule?.id,
    addedDates,
    zone: tenant.zone,
  });
  const seriesRange =
    seriesDays.length > 1
      ? [seriesDays[0], seriesDays.at(-1) as Date]
      : addedDates.length
        ? [rawDate, addedDates.at(-1) as Date]
        : null;
  const showSeriesCalendar = hasSeriesCalendarDays(calendarModifiers);
  const seriesPending =
    !readOnly && !isNewSchedule && !currentSchedule?.id && !currentScheduleError;
  const toMinutes = (value?: string) => {
    if (!value) return undefined;
    const date = toTenantDate(value);
    return getHours(date) * 60 + getMinutes(date);
  };
  const activeStart = toMinutes(active.schedule.startsAt);
  const activeEnd = toMinutes(active.schedule.endsAt);
  const timeRange =
    activeStart !== undefined && activeEnd !== undefined
      ? { start: activeStart, end: activeEnd }
      : undefined;
  const setActiveTime = ({ start, end }: TimeRange) => {
    if (!active.schedule.startsAt) return;
    const day = toTenantDate(active.schedule.startsAt);
    const at = (minutes: number) =>
      set(day, {
        hours: Math.floor(minutes / 60),
        minutes: minutes % 60,
        seconds: 0,
        milliseconds: 0,
      });
    const [startsAt, endsAt] = [at(start), at(end)];
    scheduler.setState((prev) => ({
      ...prev,
      active: {
        ...prev.active,
        schedule: {
          ...prev.active.schedule,
          startsAt: startsAt.toISOString(),
          endsAt: endsAt.toISOString(),
        },
        newValues: { ...prev.active.newValues, startsAt, endsAt },
        fresh: false,
      },
    }));
  };
  return {
    form,
    onSubmit,
    locked,
    data,
    schedulesFetching,
    schedulesLoading: !data && !schedulesError,
    removeSchedule,
    remove,
    setRemove,
    overlap,
    setOverlap,
    conflicts,
    specialtyOverride,
    setSpecialtyOverride,
    selectedSpecialties,
    setSelectedSpecialties,
    selectedForemen,
    setSelectedForemen,
    allUsers,
    setParam,
    specialties,
    fieldUsers,
    rawDate,
    setRawDate,
    active,
    scheduler,
    jobSubtitle,
    isNewSchedule,
    canScheduleMultiDay,
    canExtendMultiDay,
    multiDayPosition,
    weekendDefaults,
    seriesRange,
    calendarModifiers,
    showSeriesCalendar,
    seriesPending,
    timeRange,
    setActiveTime,
    calendarOpen,
    setCalendarOpen,
  };
};
